/**
 * The raster display contract (client round of 11 Oct 2026, Part C): how a stored pixel
 * value becomes the number a reader sees, `shown = stored × scale + offset`.
 *
 * Every `cog_titiler` layer in `/layers` carries `display: {scale, offset, unit, decimals}`
 * (`apps.datasets.display` server-side, the unit already in the reader's language). The
 * legend prints its strip ends through `displayValue`; the tiles, the stretch and every
 * classification keep the stored values. MODIS NDVI is stored ×10 000, so its `rescale`
 * of 1000,8000 prints as 0.1 to 0.8 NDVI.
 *
 * The analysis and use-case payloads (insights, AOI, zonal, UC1/UC2/UC3) are served already
 * converted, so a panel printing those numbers formats them with `displayDecimals` and
 * must not convert a second time.
 */
import type { Layer, RasterDisplay } from './api';
import { fmtCompact, fmtFixed, fmtNum } from './format';

/** The identity contract: stored values are already in the printed unit. */
export const IDENTITY_DISPLAY: RasterDisplay = { scale: 1, offset: 0, unit: '', decimals: 2 };

type Displayable = Pick<Layer, 'display'> | { display?: RasterDisplay | null } | null | undefined;

/** The layer's contract, or the identity for a layer that carries none. */
export function displayOf(layer: Displayable): RasterDisplay {
  const d = layer?.display;
  if (!d) return IDENTITY_DISPLAY;
  return {
    scale: Number.isFinite(d.scale) ? d.scale : 1,
    offset: Number.isFinite(d.offset) ? d.offset : 0,
    unit: d.unit ?? '',
    decimals: Number.isInteger(d.decimals) ? d.decimals : 2,
  };
}

/** A stored value in display units; `null` for a missing or non-finite value. */
export function displayValue(layer: Displayable, raw: number | null | undefined): number | null {
  if (raw === null || raw === undefined || !Number.isFinite(raw)) return null;
  const d = displayOf(layer);
  return raw * d.scale + d.offset;
}

/** The unit the layer prints in (`''` for an index or a class raster). */
export function displayUnit(layer: Displayable): string {
  return displayOf(layer).unit;
}

/** How many decimals the layer's numbers are printed with. */
export function displayDecimals(layer: Displayable): number {
  return displayOf(layer).decimals;
}

/**
 * A stored value converted and formatted. `trim` drops trailing zeros (the legend's strip
 * ends read "0.1" and "0.8"); without it the value has exactly `decimals` places, so a
 * column of statistics reads at one precision ("0.30", "0.42").
 */
export function fmtDisplay(
  layer: Displayable,
  raw: number | null | undefined,
  { trim = false }: { trim?: boolean } = {},
): string {
  const value = displayValue(layer, raw);
  const decimals = displayDecimals(layer);
  return trim ? fmtNum(value, decimals) : fmtFixed(value, decimals);
}

/**
 * A raster **statistic** already in display units (an insights tile, an AOI stat): the
 * magnitude letters `fmtCompact` uses from ten thousand up, and below that exactly the
 * layer's `decimals`, so NDVI reads "0.30" rather than `fmtCompact`'s rounded "0".
 */
export function fmtStatistic(value: number | null | undefined, decimals: number): string {
  if (value === null || value === undefined || !Number.isFinite(value)) return fmtNum(null);
  if (Math.abs(value) >= 10_000) return fmtCompact(value);
  return fmtFixed(value, decimals);
}

/**
 * French for the display units that are not the same word in both languages, the
 * frontend's copy of `apps.datasets.display.UNIT_FR`. A payload that is already French (the
 * layers list is served in the reader's language) passes through unchanged.
 */
const UNIT_FR: Record<string, string> = {
  'NDVI per year': 'NDVI par an',
  'thousand t': 'milliers de t',
  'mm per year': 'mm par an',
  '% of months': '% des mois',
  degrees: 'degrés',
  'risk class': 'classe de risque',
  't/ha/yr': 't/ha/an',
  'trees/ha': 'arbres/ha',
};

/** `unit` in the reader's language: French through `UNIT_FR`, anything else as it is. */
export function localisedUnit(unit: string | null | undefined, language: string): string {
  const value = unit ?? '';
  return language.startsWith('fr') ? (UNIT_FR[value] ?? value) : value;
}

/**
 * A production total in tonnes as the Productivity tile prints it (NOTES #759): "1.90" +
 * "million t" from a million up, "41.5" + "thousand t" from a thousand, else whole tonnes.
 * The digits go through `fmtNum` (locale separators); French reads "1,90 million de t",
 * "2,4 millions de t", "41,5 milliers de t".
 */
export function productionFigure(tonnes: number, language: string): { value: string; unit: string } {
  const fr = language.startsWith('fr');
  const abs = Math.abs(tonnes);
  if (abs >= 1_000_000) {
    const millions = tonnes / 1_000_000;
    const unit = fr ? (Math.abs(millions) < 2 ? 'million de t' : 'millions de t') : 'million t';
    return { value: fmtFixed(millions, 2), unit };
  }
  if (abs >= 1_000) return { value: fmtNum(tonnes / 1_000, 1), unit: localisedUnit('thousand t', language) };
  return { value: fmtNum(tonnes, 0), unit: 't' };
}
